import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { nav, site } from '../mock';

const LOGO_SRC = '/brand/gg-logo.jpg';

const navLinkClass = ({ isActive }) =>
  `text-[15px] tracking-tight font-medium transition-colors ${
    isActive ? 'text-[#1F3864]' : 'text-[#2B2B2B] hover:text-[#1F3864]'
  }`;

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
    // onScroll is stable (created inside effect); setScrolled is a stable React state setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOpen(false);
    // setOpen is a stable React state setter; we intentionally re-run only when the route path changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <header className={`sticky top-0 z-50 bg-white/95 backdrop-blur border-b transition-shadow ${scrolled ? 'shadow-sm border-slate-200' : 'border-transparent'}`}>
      {/* Top strip */}
      <div className="hidden md:block bg-[#1F3864] text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <span className="opacity-90">Trent Woods, NC • Serving practices nationwide</span>
          <div className="flex items-center gap-5">
            <a href={`mailto:${site.email}`} className="hover:text-white/80">{site.email}</a>
            <a href={site.phoneHref} className="hover:text-white/80">{site.phone}</a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" aria-label="The Garlick Group logo">
            <img src={LOGO_SRC} alt="The Garlick Group logo" className="h-11 md:h-12 w-auto select-none" draggable={false} />
            <div className="hidden xl:block leading-tight border-l border-slate-200 pl-3 ml-1">
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Cybersecurity • Compliance • vCISO</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Est. 2011 · Trent Woods, NC</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" end className={navLinkClass}>Home</NavLink>

            <div className="relative group">
              <button className="flex items-center gap-1 text-[15px] font-medium text-[#2B2B2B] hover:text-[#1F3864] transition-colors">
                Services <ChevronDown size={14} className="mt-0.5 group-hover:rotate-180 transition-transform" />
              </button>
              <div className="absolute left-0 top-full pt-3 hidden group-hover:block">
                <div className="w-[340px] bg-white border border-slate-200 shadow-xl rounded-sm py-2">
                  <Link to="/services" className="block px-4 py-2 text-[13px] uppercase tracking-widest text-slate-500 hover:text-[#1F3864]">All Services →</Link>
                  <div className="h-px bg-slate-100" />
                  {nav.services.map(s => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="block px-4 py-2.5 text-[14px] text-[#2B2B2B] hover:bg-slate-50 hover:text-[#1F3864]">{s.title}</Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="relative group">
              <button className="flex items-center gap-1 text-[15px] font-medium text-[#2B2B2B] hover:text-[#1F3864] transition-colors">
                Who We Serve <ChevronDown size={14} className="mt-0.5 group-hover:rotate-180 transition-transform" />
              </button>
              <div className="absolute left-0 top-full pt-3 hidden group-hover:block">
                <div className="w-[300px] bg-white border border-slate-200 shadow-xl rounded-sm py-2">
                  <Link to="/who-we-serve" className="block px-4 py-2 text-[13px] uppercase tracking-widest text-slate-500 hover:text-[#1F3864]">All Sectors →</Link>
                  <div className="h-px bg-slate-100" />
                  {nav.audiences.map(a => (
                    <Link key={a.slug} to={`/who-we-serve/${a.slug}`} className="block px-4 py-2.5 text-[14px] text-[#2B2B2B] hover:bg-slate-50 hover:text-[#1F3864]">{a.title}</Link>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/about" className={navLinkClass}>About</NavLink>
            <NavLink to="/articles" className={navLinkClass}>Articles</NavLink>
            <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1F3864] text-white text-[14px] font-medium px-5 py-2.5 rounded-sm hover:bg-[#17294a] transition-colors">
              Request Consultation
            </Link>
          </div>

          <button className="lg:hidden p-2" aria-label="menu" onClick={() => setOpen(v => !v)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            <Link to="/" className="block py-2 text-[15px]">Home</Link>
            <div className="py-2">
              <div className="text-[12px] uppercase tracking-widest text-slate-500 mb-1">Services</div>
              {nav.services.map(s => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="block py-1.5 pl-2 text-[14px]">{s.title}</Link>
              ))}
            </div>
            <div className="py-2">
              <div className="text-[12px] uppercase tracking-widest text-slate-500 mb-1">Who We Serve</div>
              {nav.audiences.map(a => (
                <Link key={a.slug} to={`/who-we-serve/${a.slug}`} className="block py-1.5 pl-2 text-[14px]">{a.title}</Link>
              ))}
            </div>
            <Link to="/about" className="block py-2 text-[15px]">About</Link>
            <Link to="/articles" className="block py-2 text-[15px]">Articles</Link>
            <Link to="/contact" className="block py-2 text-[15px]">Contact</Link>
            <Link to="/contact" className="mt-3 inline-flex items-center gap-2 bg-[#1F3864] text-white text-[14px] font-medium px-5 py-2.5 rounded-sm">Request Consultation</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
